import React, {
  forwardRef, useImperativeHandle, useRef,
} from 'react';
import { useFormikContext } from 'formik';

import FormCheckGroup from '~/commons/components/FormCheckGroup.jsx';
import NewLines from '~/commons/components/Newlines.jsx';
import CachedVideo from '~/commons/components/CachedVideo.jsx';
import { numToLetter } from '~/commons/helpers';
import Padding from '../layout/Padding.jsx';

import FieldsSingleSelect from './FieldsSingleSelect.jsx';
import FieldsMultiSelect from './FieldsMultiSelect.jsx';
import FieldsOrdering from './FieldsOrdering.jsx';
import FieldsMatching from './FieldsMatching.jsx';

const formatFields = {
  'single-select': FieldsSingleSelect,
  'multi-select': FieldsMultiSelect,
  ordering: FieldsOrdering,
  matching: FieldsMatching,
};

const FieldsQuestions = forwardRef(({
  answer,
  questions,
  numChar = (i)=>((i+1)+'.'),
  showAns = false,
}, ref) => {
  const { values } = useFormikContext();
  const qRefs = useRef({});

  useImperativeHandle(ref, () => ({
    scrollIntoView: (_id) => {
      qRefs.current[_id].scrollIntoView({ block: 'center' });
    },
  }));

  return (
    <Padding className="readable">
      {questions.map((q, i) => {
        const { answered, answeredSeq } = values.answers[i];
        const { format = 'single-select' } = q;
        const FieldAns = formatFields[format];
        let correctAns;
        let isCorrect;

        // get correct answer
        if (answer || showAns) {
          correctAns = format === 'single-select'
            ? q?.answer?.toString()
            : q?.answers?.map((a) => a.toString());

          if (format === 'single-select') {
            isCorrect = answered === correctAns;
          } else if (format === 'multi-select') {
            isCorrect = isEqualWith(
              answeredSeq.sort(),
              correctAns.sort(),
              (a, b) => a.toString() === b.toString(),
            );
          } else {
            isCorrect = isEqualWith(
              answeredSeq,
              correctAns,
              (a, b) => a.toString() === b.toString(),
            );
          }
        }

        return (
          <div
            key={q._id}
            className="card my-4"
            ref={(el) => { qRefs.current[q._id] = el; }}
          >
            <div className="card-body">
              <div className="row text-secondary">
                <h5 className="col-auto pr-0 question-num">
                  {numChar(i)}
                </h5>
                <h5 className="col pl-2 question-text">
                  <NewLines>{q.question}</NewLines>
                </h5>
              </div>
              {q.img && (
                <>
                  {q.img.split('.').pop().toLowerCase() == 'mp4' ? (
                  <div className="py-3 text-center question-video">
                    <CachedVideo src={q.img} controls disablePictureInPicture={true} controlslist="nodownload noplaybackrate"/>
                  </div>
                  ) : (
                  <div className="py-3 text-center question-image">
                    <img src={q.img} />
                  </div>
                  )}
                </>
              )}
              {/* answer */}
              <FieldAns
                qtnIndex={i}
                question={q}
                answered={answered}
                answeredSeq={answeredSeq}
                isCorrect={isCorrect}
                correctAns={correctAns}
                disabled={answer}
              />
            </div>
          </div>
        );
      })}
    </Padding>
  );
});

FieldsQuestions.displayName = 'FieldsQuestions';

export default FieldsQuestions;
